import React from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import * as listStyles from '../../admin-list.styles';
import * as surfaceStyles from '../../admin-surface.styles';

interface Props {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onChangePage: (page: number) => void;
}

// "1–8 of 23" with prev/next arrows, shared by the overview panels.
const ListPager = ({ page, pageSize, total, onChangePage }: Props): React.JSX.Element | null => {
  const { t } = useTranslation();

  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  // An empty list has nothing to count; a single page still shows its range, with both arrows
  // disabled, so the panel doesn't lose its footer as items are removed.
  if (total === 0) {
    return null;
  }

  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const range = t('table-range')
    .replace('[x]', from.toString())
    .replace('[y]', to.toString())
    .replace('[z]', total.toString());

  return (
    <div className={style(listStyles.pager)}>
      <span className={style(listStyles.pagerRange)}>{range}</span>
      <button
        type="button"
        className={style(surfaceStyles.pageButton(page === 0))}
        disabled={page === 0}
        aria-label={t('previous-page').toString()}
        onClick={(): void => onChangePage(page - 1)}
      >
        <ChevronLeftIcon size={20} />
      </button>
      <button
        type="button"
        className={style(surfaceStyles.pageButton(page >= lastPage))}
        disabled={page >= lastPage}
        aria-label={t('next-page').toString()}
        onClick={(): void => onChangePage(page + 1)}
      >
        <ChevronRightIcon size={20} />
      </button>
    </div>
  );
};

export default ListPager;
